// Webhook service for calling external URLs
export class WebhookService {
  // Call webhook with consultation data
  static async callWebhook(webhookUrl, data, options = {}) {
    try {
      console.log(`🔔 Calling webhook: ${webhookUrl}`);
      console.log('📤 Payload:', data);
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'WonderBeauties-Consultation/1.0',
          ...options.headers // Allow custom headers
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          source: 'wonder-beauties-consultation',
          version: '1.0',
          ...data // Spread the data you want to send
        })
      });

      if (!response.ok) {
        throw new Error(`Webhook call failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json().catch(() => ({ status: 'received' })); // Some webhooks don't return JSON
      console.log('✅ Webhook call successful:', result);
      
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ Webhook call failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Call webhook with retry logic and exponential backoff
   * @param {string} webhookUrl - The webhook URL to call
   * @param {object} data - The data to send
   * @param {object} options - Options including maxRetries, onRetry callback, submissionId
   * @returns {Promise<{success: boolean, data?: any, error?: string, attempts: number}>}
   */
  static async callWebhookWithRetry(webhookUrl, data, options = {}) {
    const {
      maxRetries = 3,
      onRetry = null, // Callback for retry events: (attemptNum, delay, error) => {}
      onStatusChange = null, // Callback for status updates: (status, info) => {}
      headers = {},
      submissionId = null
    } = options;

    let lastError = null;
    
    // Add submission_id to payload if provided
    const payload = submissionId ? { ...data, submission_id: submissionId } : data;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔔 Webhook attempt ${attempt}/${maxRetries} for submission: ${submissionId || 'N/A'}`);
        
        // Notify status change
        if (onStatusChange) {
          onStatusChange('attempting', { attempt, maxRetries, submissionId });
        }

        const result = await this.callWebhook(webhookUrl, payload, { headers });
        
        if (result.success) {
          console.log(`✅ Webhook succeeded on attempt ${attempt}`);
          
          // Notify success
          if (onStatusChange) {
            onStatusChange('success', { attempt, submissionId, data: result.data });
          }
          
          return {
            success: true,
            data: result.data,
            attempts: attempt
          };
        }
        
        // If callWebhook returned success:false, treat as error
        lastError = result.error;
        throw new Error(result.error || 'Webhook call failed');
        
      } catch (error) {
        lastError = error.message;
        console.error(`❌ Webhook attempt ${attempt}/${maxRetries} failed:`, error.message);
        
        // If this is not the last attempt, retry with exponential backoff
        if (attempt < maxRetries) {
          const delaySeconds = Math.pow(2, attempt - 1); // 1s, 2s, 4s
          const delayMs = delaySeconds * 1000;
          
          console.log(`⏳ Retrying in ${delaySeconds}s...`);
          
          // Notify retry
          if (onRetry) {
            onRetry(attempt, delaySeconds, error.message);
          }
          
          if (onStatusChange) {
            onStatusChange('retrying', { attempt, nextAttempt: attempt + 1, delay: delaySeconds, error: error.message });
          }
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    
    // All retries exhausted
    console.error(`❌ Webhook failed after ${maxRetries} attempts. Last error:`, lastError);
    
    // Notify final failure
    if (onStatusChange) {
      onStatusChange('failed', { attempts: maxRetries, error: lastError, submissionId });
    }
    
    return {
      success: false,
      error: lastError || 'Unknown error',
      attempts: maxRetries
    };
  }

  // Call webhook for "Start Consultation" button
  static async callHelpRequestWebhook(consultationData) {
    const webhookUrl = process.env.REACT_APP_HELP_WEBHOOK_URL;
    
    if (!webhookUrl) {
      console.warn('No help webhook URL configured in .env.local');
      return { success: false, error: 'No webhook URL configured' };
    }
    
    return await this.callWebhook(webhookUrl, {
      event_type: 'consultation_started',
      action: 'consultation_started',
      user_choice: 'start_consultation',
      button_text: 'ابدئي الاستشارة ✨',
      user_id: consultationData.userId,
      consultation_id: consultationData.consultationId,
      session_data: {
        user_info: consultationData.formData,
        has_uploaded_image: consultationData.hasImage,
        consultation_step: 'consultation_start'
      },
      budget_selection: consultationData.budgetSelection || null,
      // Channel mapping for multi-channel support (Respond.io)
      channel_mapping: {
        contact_id: consultationData.channelParams?.contact_id || null,
        channel_id: consultationData.channelParams?.channel_id || null,
        has_channel_info: !!(consultationData.channelParams?.contact_id && consultationData.channelParams?.channel_id)
      },
      metadata: {
        user_agent: navigator.userAgent,
        timestamp: consultationData.timestamp,
        page_url: window.location.href,
        db_save_confirmed: true // Signal to n8n that data is already saved in DB
      }
    });
  }

  // Call webhook for "No" button in AI assistance  
  static async callDeclineWebhook(consultationData) {
    const webhookUrl = process.env.REACT_APP_DECLINE_WEBHOOK_URL;
    
    if (!webhookUrl) {
      console.warn('No decline webhook URL configured in .env.local');
      return { success: false, error: 'No webhook URL configured' };
    }
    
    return await this.callWebhook(webhookUrl, {
      event_type: 'ai_assistance_declined',
      action: 'ai_assistance_declined',
      user_choice: 'no_ai_help',
      button_text: 'لا :(',
      user_id: consultationData.userId,
      consultation_id: consultationData.consultationId,
      session_data: {
        user_info: consultationData.formData,
        has_uploaded_image: consultationData.hasImage,
        consultation_step: 'ai_assistance_prompt'
      },
      // Channel mapping for multi-channel support (Respond.io)
      channel_mapping: {
        contact_id: consultationData.channelParams?.contact_id || null,
        channel_id: consultationData.channelParams?.channel_id || null,
        has_channel_info: !!(consultationData.channelParams?.contact_id && consultationData.channelParams?.channel_id)
      },
      metadata: {
        user_agent: navigator.userAgent,
        timestamp: consultationData.timestamp,
        page_url: window.location.href
      }
    });
  }

  // Call webhook for "Next" button after image upload
  // Supports optional retry behavior (default 1 attempt to preserve existing flow behavior)
  static async callImageUploadWebhook(consultationData, options = {}) {
    const webhookUrl = process.env.REACT_APP_IMAGE_UPLOAD_WEBHOOK_URL;
    const {
      maxRetries = 1,
      onStatusChange = null,
      onRetry = null,
      headers = {},
      submissionId = null
    } = options;
    
    if (!webhookUrl) {
      console.warn('No image upload webhook URL configured in .env.local');
      return { success: false, error: 'No webhook URL configured' };
    }

    const payload = {
      event_type: 'image_uploaded_proceed',
      action: 'image_uploaded_proceed',
      user_choice: 'proceed_after_image',
      button_text: 'التالي ✨',
      user_id: consultationData.userId,
      consultation_id: consultationData.consultationId,
      image_validation_request_id: consultationData.imageValidationRequestId || null,
      session_data: {
        user_info: consultationData.formData, // Empty in step 1
        has_uploaded_image: consultationData.hasImage,
        consultation_step: 'image_upload_completed'
      },
      // Channel mapping for multi-channel support (Respond.io)
      channel_mapping: {
        contact_id: consultationData.channelParams?.contact_id || null,
        channel_id: consultationData.channelParams?.channel_id || null,
        has_channel_info: !!(consultationData.channelParams?.contact_id && consultationData.channelParams?.channel_id)
      },
      metadata: {
        user_agent: navigator.userAgent,
        timestamp: consultationData.timestamp,
        page_url: window.location.href
      }
    };

    return await this.callWebhookWithRetry(webhookUrl, payload, {
      maxRetries,
      onStatusChange,
      onRetry,
      headers,
      submissionId
    });
  }

  // Call webhook when pharmacist comes online (triggers queue dispatch in n8n)
  static async callPharmacistOnlineWebhook(roundRobinId) {
    const webhookUrl = process.env.REACT_APP_PHARMACIST_ONLINE_WEBHOOK_URL;

    if (!webhookUrl) {
      console.warn('⚠️ Pharmacist online webhook URL not configured');
      return { success: false, error: 'Webhook URL not configured' };
    }

    console.log(`📤 Triggering pharmacist online webhook for round_robin_id: ${roundRobinId}`);

    return await this.callWebhook(webhookUrl, {
      round_robin_id: roundRobinId
    });
  }

  // Generic webhook call for any button click (for future use)
  static async callButtonClickWebhook(buttonInfo, consultationData) {
    const webhookUrl = buttonInfo.webhookUrl || process.env.REACT_APP_GENERIC_WEBHOOK_URL;
    
    if (!webhookUrl) {
      console.warn('No webhook URL configured for button:', buttonInfo.buttonId);
      return { success: false, error: 'No webhook URL configured' };
    }
    
    return await this.callWebhook(webhookUrl, {
      event_type: 'button_click',
      action: buttonInfo.action,
      user_choice: buttonInfo.choice,
      button_text: buttonInfo.text,
      button_id: buttonInfo.buttonId,
      user_id: consultationData.userId,
      consultation_id: consultationData.consultationId,
      session_data: {
        user_info: consultationData.formData,
        has_uploaded_image: consultationData.hasImage,
        consultation_step: buttonInfo.step || 'unknown'
      },
      // Channel mapping for multi-channel support (Respond.io)
      channel_mapping: {
        contact_id: consultationData.channelParams?.contact_id || null,
        channel_id: consultationData.channelParams?.channel_id || null,
        has_channel_info: !!(consultationData.channelParams?.contact_id && consultationData.channelParams?.channel_id)
      },
      metadata: {
        user_agent: navigator.userAgent,
        timestamp: consultationData.timestamp,
        page_url: window.location.href
      }
    });
  }
}
